import {
  ApiOutlined,
  AuditOutlined,
  DashboardOutlined,
  DownloadOutlined,
  FileTextOutlined,
  LogoutOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { type Me, ROLE_LABEL } from '@serievo/contracts';
import { Alert, Button, Dropdown, Layout, Menu, Result, Spin, Tag, Typography } from 'antd';
import { type ReactNode, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useLogout } from '../api/auth';
import { ApiError } from '../api/client';
import { useContextQuery } from '../api/context';
import { ContextBar } from '../features/context/ContextBar';
import { useWorkContext } from '../features/context/useWorkContext';
import { buildQuery, permsFor, type Work, WorkProvider } from '../features/context/WorkContext';
import { Brand } from './Brand';

export const NAV = [
  { key: '/', icon: <DashboardOutlined />, label: 'Tổng quan' },
  { key: '/hoa-don', icon: <FileTextOutlined />, label: 'Quản lý hóa đơn' },
  { key: '/doi-soat', icon: <AuditOutlined />, label: 'Đối soát R1' },
  { key: '/dong-tien', icon: <WalletOutlined />, label: 'Dòng tiền' },
  { key: '/xuat-file', icon: <DownloadOutlined />, label: 'Xuất Excel' },
  { key: '/ket-noi', icon: <ApiOutlined />, label: 'Kết nối shop' },
] as const;

export function AppShell({ me, children }: { me: Me; children: ReactNode }) {
  const ctx = useContextQuery(true);
  const work = useWorkContext(ctx.data);
  const logout = useLogout();
  const loc = useLocation();
  const navigate = useNavigate();

  const value = useMemo<Work | null>(() => {
    if (!work.tenant || !work.entity || !work.selection.period) return null;
    return {
      tenant: work.tenant,
      entity: work.entity,
      ...(work.shop ? { shop: work.shop } : {}),
      period: work.selection.period,
      query: buildQuery(work.entity.id, work.selection.period, work.shop?.id),
      perms: permsFor(me.grants, work.tenant.id, work.entity.id),
    };
  }, [work, me.grants]);

  const roles = [...new Set(me.grants.filter((g) => g.tenantId === work.tenant?.id).map((g) => ROLE_LABEL[g.role]))];
  const selected = NAV.find((n) => n.key !== '/' && loc.pathname.startsWith(n.key))?.key ?? '/';

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Header className="app-header">
        <Brand />
        <Typography.Text className="tagline" style={{ color: '#C3CAD3', fontSize: 12 }}>
          Kế toán thuế cho shop Shopee, TikTok
        </Typography.Text>
        <div style={{ flex: 1 }} />
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              { key: 'who', label: me.user.email, disabled: true },
              { type: 'divider' },
              { key: 'logout', icon: <LogoutOutlined />, label: 'Đăng xuất', onClick: () => logout.mutate() },
            ],
          }}
        >
          <Button type="text" icon={<UserOutlined />} style={{ color: '#E6EAEF' }} aria-label="Tài khoản">
            <span className="tagline">{me.user.name ?? me.user.email}</span>
            {roles.length > 0 && (
              <Tag bordered={false} style={{ marginInlineStart: 6 }}>
                {roles.join(', ')}
              </Tag>
            )}
          </Button>
        </Dropdown>
      </Layout.Header>
      <Layout>
        <Layout.Sider width={196} breakpoint="lg" collapsedWidth={56} theme="light" className="app-sider">
          <Menu
            mode="inline"
            selectedKeys={[selected]}
            items={NAV.map((n) => ({ key: n.key, icon: n.icon, label: n.label }))}
            onClick={({ key }) => navigate({ pathname: key, search: loc.search })}
          />
        </Layout.Sider>
        <Layout.Content style={{ minWidth: 0 }}>
          {ctx.isPending ? (
            <Spin style={{ display: 'block', marginTop: 64 }} size="large" />
          ) : ctx.error ? (
            <Alert
              style={{ margin: 16 }}
              type="error"
              showIcon
              message="Không tải được danh sách công ty"
              description={`${ctx.error.message}${ctx.error instanceof ApiError && ctx.error.correlationId ? ` · mã ${ctx.error.correlationId}` : ''}`}
            />
          ) : ctx.data.length === 0 || !value ? (
            <Result status="info" title="Tài khoản chưa được gán công ty hoặc pháp nhân nào" />
          ) : (
            <>
              <ContextBar tenants={ctx.data} selection={work.selection} onChange={work.change} loading={ctx.isFetching} />
              {value.tenant.isDemo && (
                <Alert
                  banner
                  type="info"
                  message="Công ty demo: toàn bộ số liệu là dữ liệu giả lập, không phải dữ liệu của doanh nghiệp thật."
                />
              )}
              <WorkProvider value={value}>{children}</WorkProvider>
            </>
          )}
        </Layout.Content>
      </Layout>
    </Layout>
  );
}
